import { Any, Id } from '@common/defs/types';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { User } from '@modules/users/defs/types';
import { useCallback, useMemo } from 'react';
import { useFacebookCookies } from '@common/hooks/useFacebookCookies';

export const B2C_BOOKING_VALUE = 20;
export const B2C_CTA_VALUE = 5;
export const B2C_ZIP_SUBMIT_VALUE = 1.5;
export const B2C_CURRENCY = 'EUR';

/**
 * Type pour les données utilisateur/visiteur utilisables par le Pixel Meta.
 * Peut être un User inscrit ou un simple visiteur/lead.
 */
export interface MetaUserData {
  id?: Id;
  email?: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
  address?: string;
  city?: string;
  zip?: string | { code?: string };
}

const mapUserForPixel = (userData: MetaUserData | User) => {
  const onlyDigits = (s?: string) => (s || '').replace(/\D/g, '');
  // zip peut être un string (lead) ou un objet { code } (User)
  const zipCode =
    typeof userData.zip === 'string' ? userData.zip : (userData.zip as { code?: string })?.code;
  return {
    external_id: userData.id ?? undefined,
    em: userData.email?.toLowerCase() ?? undefined,
    fn: userData.firstname?.toLowerCase() ?? undefined,
    ln: userData.lastname?.toLowerCase() ?? undefined,
    ph: onlyDigits(userData.phone) ?? undefined,
    ad: userData.address ?? undefined,
    ct: userData.city?.toLowerCase().replace(/\s+/g, '-') ?? undefined,
    zp: zipCode ?? undefined,
    country: 'fr',
  };
};

export interface UseMetaTrackingProps {
  datasetId?: string;
  additionalDatasetIds?: string[];
}

const useMetaTracking = (props: UseMetaTrackingProps = {}) => {
  const { user } = useAuth();
  const DATASET_ID = props.datasetId || process.env.NEXT_PUBLIC_FACEBOOK_DATASET_ID_B2B || '';

  // All pixel IDs to init & track (primary + additional, deduplicated)
  const additionalKey = (props.additionalDatasetIds || []).join(',');
  const allDatasetIds = useMemo(() => {
    const ids = [DATASET_ID];
    if (additionalKey) {
      for (const id of additionalKey.split(',')) {
        if (id && id !== DATASET_ID) {
          ids.push(id);
        }
      }
    }
    return ids;
  }, [DATASET_ID, additionalKey]);

  const { fbc, fbp, ready: fbCookiesReady } = useFacebookCookies();

  const init = useCallback(
    (userData?: MetaUserData | User) => {
      const doInit = () => {
        for (const id of allDatasetIds) {
          if (userData) {
            window.fbq('init', id, mapUserForPixel(userData));
          } else {
            window.fbq('init', id);
          }
        }
        trackEvent('PageView');
      };

      if (typeof window === 'undefined') {
        return;
      }
      if (typeof window.fbq === 'function') {
        doInit();
        return;
      }

      let tries = 0;
      const iv = setInterval(() => {
        tries++;
        if (typeof window.fbq === 'function') {
          doInit();
          clearInterval(iv);
        }
        if (tries >= 20) {
          clearInterval(iv);
        }
      }, 100);
    },
    [allDatasetIds]
  );

  const updateUser = useCallback(
    (userData: MetaUserData | User) => {
      if (!window.fbq || !window.fbq.instance || !window.fbq.instance.pixelsByID) {
        return;
      }
      for (const id of allDatasetIds) {
        if (window.fbq.instance.pixelsByID[id]) {
          window.fbq.instance.pixelsByID[id].userData = mapUserForPixel(userData);
        }
      }
    },
    [allDatasetIds]
  );

  const trackEvent = useCallback(
    (eventName: string, params: Record<string, Any> = {}) => {
      // Les PII (em, fn, ln, ph, zp, ct, ad, country, external_id) ne doivent PAS
      // être dans les event params : elles passent uniquement via fbq('init', ..., userData)
      // où le SDK les hash automatiquement en SHA-256.
      // Ici on ne garde que les données business (value, currency, content_ids, etc.)

      // Extraire eventID pour le passer séparément (déduplication Meta)
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { eventID, event_id: eventIdSnake, ...restParams } = params;
      const finalEventId = eventID || eventIdSnake;

      // Options pour Meta (eventID pour déduplication)
      const options: Record<string, Any> = {};
      if (finalEventId) {
        options.eventID = String(finalEventId);
      }

      const STANDARD_EVENTS = [
        'PageView',
        'Lead',
        'Schedule',
        'Purchase',
        'CompleteRegistration',
        'ViewContent',
        'AddToCart',
        'InitiateCheckout',
        'Search',
        'Contact',
        'SubmitApplication',
      ];
      const isStandard = STANDARD_EVENTS.includes(eventName);
      const method = isStandard ? 'trackSingle' : 'trackSingleCustom';

      const call = () => {
        if (!window.fbq) {
          return;
        }

        for (const id of allDatasetIds) {
          if (finalEventId) {
            window.fbq(method, id, eventName, restParams, options);
          } else {
            window.fbq(method, id, eventName, restParams);
          }
        }
      };

      if (typeof window === 'undefined') {
        return;
      }
      if (typeof window.fbq === 'function') {
        call();
        return;
      }

      // retry jusqu'à 2s max
      let tries = 0;
      const iv = setInterval(() => {
        tries++;
        if (typeof window.fbq === 'function') {
          call();
          clearInterval(iv);
        }
        if (tries >= 20) {
          clearInterval(iv);
        }
      }, 100);
    },
    [user, allDatasetIds]
  );

  return {
    init,
    trackEvent,
    updateUser,
    fbc,
    fbp,
    fbCookiesReady,
  };
};

export default useMetaTracking;
